import { STANDARD_ERROR_MESSAGE } from "../constants";
import getClientSideCSRF from "../getCSRF";
import { getApiUrl } from "./util";

type RequestOptions = Omit<RequestInit, "body"> & { body?: any; params?: any };

interface ApiClient {
  get(url: string, config?: RequestOptions): Promise<any>;
  post(url: string, data?: any, config?: RequestOptions): Promise<any>;
  patch(url: string, data?: any, config?: RequestOptions): Promise<any>;
  delete(url: string, config?: RequestOptions): Promise<any>;
}

export type ApiError = {
  data: Record<string, string | string[]>;
  statusCode: number;
};

export function isStandardApiError(error: any): error is ApiError {
  return (
    error &&
    (typeof error.data === "object" || typeof error.data === "string") &&
    typeof error.statusCode === "number"
  );
}

const createApiClient = (appPath?: string): ApiClient => {
  const baseUrl = getApiUrl(appPath).replace(/\/$/, "");

  const request = async (
    url: string,
    options: RequestOptions = {}
  ): Promise<any> => {
    const { body, params, ...rest } = options;

    // Build query string if params exist
    let fullUrl = `${baseUrl}/${url.replace(/^\/+/, "")}`;
    if (params && Object.keys(params).length > 0) {
      const query = new URLSearchParams(params).toString();
      fullUrl += `?${query}`;
    }

    const csrfToken = (await getClientSideCSRF()) || "";
    const headers = {
      "X-CSRFToken": csrfToken,
      "Content-Type": "application/json",
      ...rest.headers,
    };

    const response = await fetch(`${fullUrl}`, {
      ...rest,
      body: body ? JSON.stringify(body) : undefined,
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      let errorData = null;
      try {
        errorData = await response.json();
      } catch {
        errorData = STANDARD_ERROR_MESSAGE;
      }
      throw {
        data: errorData,
        statusCode: response.status,
      };
    }

    if (response.status === 204) {
      return null;
    }

    return await response.json();
  };

  return {
    get(url, config) {
      return request(url, { ...config, method: "GET" });
    },
    post(url, data, config) {
      return request(url, { ...config, method: "POST", body: data });
    },
    patch(url, data, config) {
      return request(url, { ...config, method: "PATCH", body: data });
    },
    delete(url, config) {
      return request(url, { ...config, method: "DELETE" });
    },
  };
};

export default createApiClient;

export function convertArrayValuesToStrings(
  obj: Record<string, string | string[]> | string
): Record<string, string> | string {
  if (typeof obj == "string") return obj;

  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (Array.isArray(value)) {
      // If the value is an array, join the elements
      result[key] = value.join("; ");
    } else {
      // If the value is a string, use it directly
      result[key] = value;
    }
  }

  return result;
}
